/**
 * A utility class for handling sessionStorage operations with type safety and error handling
 */
export class SessionStorage {
  /**
   * Get an item from sessionStorage
   * @param key The key to retrieve
   * @returns The parsed value or null if not found
   */
  static get<T>(key: string): T | null {
    if (typeof window === 'undefined') return null

    try {
      const item = sessionStorage.getItem(key)
      if (!item) return null

      const data = JSON.parse(item)

      // Check if data has expiry and is expired
      if (data.expiry && new Date().getTime() > data.expiry) {
        sessionStorage.removeItem(key)
        return null
      }

      return data.value ?? null
    } catch (error) {
      console.error(`Error getting '${key}' from sessionStorage:`, error)
      return null
    }
  }

  /**
   * Save an item to sessionStorage
   * @param key The key to store under
   * @param value The value to store
   * @returns true if saved successfully, false otherwise
   */
  static set<T>(key: string, value: T): boolean {
    if (typeof window === 'undefined') return false

    try {
      const data = {
        value,
        expiry: null,
      }
      sessionStorage.setItem(key, JSON.stringify(data))
      return true
    } catch (error) {
      console.error(`Error saving '${key}' to sessionStorage:`, error)
      return false
    }
  }

  /**
   * Save an item to sessionStorage with expiry time
   * @param key The key to store under
   * @param value The value to store
   * @param expiryInMinutes Number of minutes until the item expires
   * @returns true if saved successfully, false otherwise
   */
  static setWithExpiry<T>(
    key: string,
    value: T,
    expiryInMinutes: number
  ): boolean {
    if (typeof window === 'undefined') return false

    try {
      const data = {
        value,
        expiry: new Date().getTime() + expiryInMinutes * 60 * 1000,
      }
      sessionStorage.setItem(key, JSON.stringify(data))
      return true
    } catch (error) {
      console.error(`Error saving '${key}' to sessionStorage:`, error)
      return false
    }
  }

  /**
   * Remove an item from sessionStorage
   * @param key The key to remove
   * @returns true if removed successfully, false otherwise
   */
  static remove(key: string): boolean {
    if (typeof window === 'undefined') return false

    try {
      sessionStorage.removeItem(key)
      return true
    } catch (error) {
      console.error(`Error removing '${key}' from sessionStorage:`, error)
      return false
    }
  }

  /**
   * Clear all items from sessionStorage
   * @returns true if cleared successfully, false otherwise
   */
  static clear(): boolean {
    if (typeof window === 'undefined') return false

    try {
      sessionStorage.clear()
      return true
    } catch (error) {
      console.error('Error clearing sessionStorage:', error)
      return false
    }
  }

  /**
   * Check if a key exists in sessionStorage and is not expired
   * @param key The key to check
   * @returns true if key exists and not expired, false otherwise
   */
  static has(key: string): boolean {
    if (typeof window === 'undefined') return false

    try {
      const item = sessionStorage.getItem(key)
      if (!item) return false

      const data = JSON.parse(item)
      if (data.expiry && new Date().getTime() > data.expiry) {
        sessionStorage.removeItem(key)
        return false
      }

      return true
    } catch {
      return false
    }
  }
}
